import "../../../i18n/i18n";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { useAppStyles } from "../../styles/appStyles";
import { Button, Text, Toolbar, ToolbarButton, Tooltip, Drawer, DrawerHeader, DrawerHeaderTitle, DrawerBody } from "@fluentui/react-components";
import { Open16Regular, QuestionCircle24Regular, ArrowLeft20Regular, Dismiss24Regular, ShieldTask24Regular, Document24Regular, ShieldCheckmark24Regular, DocumentText24Regular } from "@fluentui/react-icons";
import {
  HelpUrl_how_to_start_free,
  HelpUrl_how_to_subscribe,
  HelpUrl_how_to_start_paid,
  HelpUrl_bdo_credentials,
  HelpUrl_dictionaries_wasteprocess,
  HelpUrl_dictionaries_wastecode,
  HelpUrl_keo_annual_report_with_permit,
  HelpUrl_keo_annualy,
  HelpUrl_keo_monthly_statement,
  HelpUrl_kpo_confirmed,
  HelpUrl_kpo_status_overview,
  HelpUrl_privacy,
  HelpUrl_terms_of_use,
  HelpUrl_kpo_card_print,
  HelpUrl_keo_card_print
} from "../../../config/helplinks";
import { APP_VERSION } from "../../../config/version";

// Funkcja do obsługi linków w Excel Web
const handleExternalLink = async (url: string, t: (key: string) => string) => {
  try {
    // Sprawdź czy jest w Excel Web
    const platform = Office?.context?.platform;
    const isExcelWeb = platform === Office.PlatformType.OfficeOnline;

    if (isExcelWeb) {
      // W Excel Web skopiuj URL do schowka
      await navigator.clipboard.writeText(url);

      // Wyświetl powiadomienie
      Office.context.ui.displayDialogAsync(
        `data:text/html,
        <html>
          <head>
            <style>
              body { font-family: 'Segoe UI', sans-serif; padding: 20px; }
              .container { max-width: 400px; margin: 0 auto; text-align: center; }
              .url { background: #f3f2f1; padding: 10px; border-radius: 4px; margin: 10px 0; word-break: break-all; }
            </style>
          </head>
          <body>
            <div class="container">
              <h3>${t("info_dialog_link_copied_title")}</h3>
              <p>${t("info_dialog_excel_web_message")}</p>
              <div class="url">${url}</div>
              <p>${t("info_dialog_paste_instruction")}</p>
            </div>
          </body>
        </html>`,
        { height: 300, width: 500 }
      );
    } else {
      // W Excel Desktop otwórz normalnie
      window.open(url, '_blank');
    }
  } catch (error) {
    console.error('Error handling external link:', error);
    // Fallback - spróbuj otworzyć normalnie
    window.open(url, '_blank');
  }
};

// Typ dla linków pomocy
interface HelpLink {
  nameKey: string;
  url: string;
  isInternal?: boolean;
}
//Lista linków jak rozpocząć pracę z dodatkiem
export const GettingStartedLinks: HelpLink[] = [
  { nameKey: "help_link_how_to_start_free", url: HelpUrl_how_to_start_free },
  { nameKey: "help_link_how_to_subscribe", url: HelpUrl_how_to_subscribe },
  { nameKey: "help_link_how_to_start_paid", url: HelpUrl_how_to_start_paid },
  { nameKey: "help_link_bdo_credentials", url: HelpUrl_bdo_credentials },
];

//lista linków pomocy. Nazwa linku plus lonk do strony zewnętrzenj
export const HelpLinks: HelpLink[] = [
  { nameKey: "help_link_kpo_status_overview", url: HelpUrl_kpo_status_overview },
  { nameKey: "help_link_kpo_confirmed", url: HelpUrl_kpo_confirmed },
  { nameKey: "help_link_keo_annually", url: HelpUrl_keo_annualy },
  { nameKey: "help_link_kpo_view", url: HelpUrl_kpo_card_print },
  { nameKey: "help_link_keo_view", url: HelpUrl_keo_card_print },

  { nameKey: "help_link_monthly_statement", url: HelpUrl_keo_monthly_statement },
  { nameKey: "help_link_annual_report", url: HelpUrl_keo_annual_report_with_permit },
  { nameKey: "help_link_waste_catalog", url: HelpUrl_dictionaries_wastecode },
  { nameKey: "help_link_waste_processes", url: HelpUrl_dictionaries_wasteprocess },
];


interface InfoPageProps {
  onBack: () => void;
}

const InfoPage: React.FC<InfoPageProps> = ({ onBack }) => {
  const styles = useAppStyles();
  const { t } = useTranslation();
  const [isPrivacyDrawerOpen, setIsPrivacyDrawerOpen] = React.useState(false);

  return (
    <div className={styles.appPageContainer}>
      <div className={styles.appPageHeader}>
        <QuestionCircle24Regular className={styles.appPageHeaderIcon} />
        <Text className={styles.appPageTitle}>
          {t("info_page_title", "Pomoc")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip content={t("info_page.toolbar.back_tooltip", "Powrót do poprzedniej strony")} relationship="description">
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("info_page.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={onBack}
          >
            {t("info_page.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>

      <div className={styles.appPageContent}>

      <Text className={styles.appSectionTitle}>{t("getting_started_links_title", "Jak rozpocząć pracę z dodatkiem")}</Text>
        {/* Linki do rozpoczęcia pracy z dodatkiem - GettingStartedLinks */}
        {GettingStartedLinks.map((link) => (
          <div key={link.nameKey} className={styles.appLinkParagraph}>
            <Button
              appearance="transparent"
              as="a"
              onClick={(e) => {
                e.preventDefault();
                handleExternalLink(link.url, t);
              }}
              className={styles.appLinkButton}
            >
              {t(link.nameKey)}
              <Open16Regular className={styles.appExternalLinkIcon} />
            </Button>
          </div>
        ))}

        <Text className={styles.appSectionTitle}>{t("help_links_title", "Linki pomocy")}</Text>
        {/* Link do strony pomocy - HelpLinks */}
        {HelpLinks.map((link) => (
          <div key={link.nameKey} className={styles.appLinkParagraph}>
            <Button
              appearance="transparent"
              as="a"
              onClick={(e) => {
                e.preventDefault();
                handleExternalLink(link.url, t);
              }}
              className={styles.appLinkButton}
            >
              {t(link.nameKey)}
              <Open16Regular className={styles.appExternalLinkIcon} />
            </Button>
          </div>
        ))}

        {/* Linia rozdzielająca */}
        <div style={{ borderTop: '1px solid #e1dfdd', margin: '20px 0' }}></div>

        {/* Sekcja informacji o przetwarzaniu danych */}
        <div className={styles.appLinkParagraph}>
          <Button
            appearance="transparent"
            icon={<ShieldTask24Regular />}
            onClick={() => setIsPrivacyDrawerOpen(true)}
            className={styles.appLinkButton}
          >
            {t("info_link_data_processing", "Jak aplikacja korzysta z danych użytkownika")}
          </Button>
        </div>

        {/* Sekcja z linkami zewnętrznymi */}
        <div className={styles.appLinkParagraph}>
          <Button
            appearance="transparent"
            as="a"
            href="https://carbowaste.carbomath.com/carbowaste-bdo"
            target="_blank"
            rel="noopener noreferrer"
            icon={<Document24Regular />}
            className={styles.appLinkButton}
          >
            {t("info_link_app_description", "Opis aplikacji")}
            <Open16Regular className={styles.appExternalLinkIcon} />
          </Button>
        </div>

        <div className={styles.appLinkParagraph}>
          <Button
            appearance="transparent"
            as="a"
            href={HelpUrl_privacy}
            target="_blank"
            rel="noopener noreferrer"
            icon={<ShieldCheckmark24Regular />}
            className={styles.appLinkButton}
          >
            {t("info_link_privacy_policy_short")}
            <Open16Regular className={styles.appExternalLinkIcon} />
          </Button>
        </div>

        <div className={styles.appLinkParagraph}>
          <Button
            appearance="transparent"
            as="a"
            href={HelpUrl_terms_of_use}
            target="_blank"
            rel="noopener noreferrer"
            icon={<DocumentText24Regular />}
            className={styles.appLinkButton}
          >
            {t("info_link_terms_of_use_short")}
            <Open16Regular className={styles.appExternalLinkIcon} />
          </Button>
        </div>

        {/* Wersja aplikacji */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Text style={{ fontSize: '12px', color: '#605e5c' }}>
            Wersja aplikacji: {APP_VERSION}
          </Text>
        </div>

        
      </div>

      {/* Drawer z informacją o przetwarzaniu danych */}
      <Drawer
        type="overlay"
        separator
        open={isPrivacyDrawerOpen}
        onOpenChange={(_, { open }) => setIsPrivacyDrawerOpen(open)}
        position="end"
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t("common_close", "Zamknij")}
                icon={<Dismiss24Regular />}
                onClick={() => setIsPrivacyDrawerOpen(false)}
              />
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldTask24Regular />
              <span>{t("info_drawer_data_processing_title", "Przetwarzanie danych użytkownika")}</span>
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          <div style={{ padding: '16px' }}>
            <Text style={{ display: 'block', marginBottom: '16px', lineHeight: '1.6' }}>
              {t("info_drawer_data_processing_intro", "Aplikacja CARBOwaste BDO szanuje Twoją prywatność i bezpieczeństwo danych.")}
            </Text>

            <Text style={{ display: 'block', marginBottom: '16px', fontWeight: 600 }}>
              {t("info_drawer_data_processing_subtitle", "Aplikacja NIE przetwarza Twoich danych osobowych")}
            </Text>

            <Text style={{ display: 'block', marginBottom: '12px', lineHeight: '1.6' }}>
              {t("info_drawer_data_processing_description", "Aplikacja wykorzystuje bezpieczne rozwiązania Microsoft 365, aby:")}
            </Text>

            <ul style={{ marginBottom: '16px', lineHeight: '1.8' }}>
              <li>
                <Text>
                  {t("info_drawer_data_processing_point1", "Sprawdzić, czy jesteś zalogowany kontem Microsoft 365")}
                </Text>
              </li>
              <li>
                <Text>
                  {t("info_drawer_data_processing_point2", "Odczytać tylko Twój adres email oraz imię i nazwisko - wyłącznie w celu wypełnienia formularza kontaktowego w aplikacji")}
                </Text>
              </li>
            </ul>

            <Text style={{ display: 'block', marginBottom: '16px', lineHeight: '1.6', fontStyle: 'italic' }}>
              {t("info_drawer_data_processing_note", "Dane te są wykorzystywane wyłącznie lokalnie i nie są przesyłane ani przechowywane na serwerach aplikacji bez Twojej wyraźnej zgody (np. podczas wysyłania formularza kontaktowego).")}
            </Text>

            <Text style={{ display: 'block', marginBottom: '16px', lineHeight: '1.6' }}>
              {t("info_drawer_data_processing_security", "Wszystkie operacje wykonywane są z wykorzystaniem bezpiecznych, certyfikowanych mechanizmów autoryzacji Microsoft 365, które gwarantują najwyższy poziom ochrony Twoich danych.")}
            </Text>

            <Text style={{ display: 'block', lineHeight: '1.6', fontSize: '14px', color: '#605e5c' }}>
              {t("info_drawer_data_processing_more_info", "Więcej informacji na temat przetwarzania danych znajdziesz w naszej Polityce Prywatności.")}
            </Text>

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
              <Button
                appearance="primary"
                onClick={() => setIsPrivacyDrawerOpen(false)}
              >
                {t("common_close", "Zamknij")}
              </Button>
            </div>
          </div>
        </DrawerBody>
      </Drawer>
    </div>
  );
};

export default InfoPage;
